/** QuickPlan — Bibliothèque intégrée : structure aluminium à rainure (séries 20, 30, 40, 45).
 *
 * Profilé : extrudé le long de X depuis l'origine (0 → L), section centrée sur l'axe
 * (Y = largeur, Z = hauteur). Ports « debut » et « fin » aux extrémités de l'axe.
 * Prix indicatifs (€ HT) — à ajuster dans un modèle de projet si besoin.
 */

const SERIES = [
  { s: 20, sizes: [[1, 1], [1, 2]], pm: { '1x1': 6, '1x2': 10 }, screw: 'M5', nut: 'Écrou marteau M5 rainure 6' },
  { s: 30, sizes: [[1, 1], [1, 2]], pm: { '1x1': 9, '1x2': 15 }, screw: 'M6', nut: 'Écrou marteau M6 rainure 8' },
  { s: 40, sizes: [[1, 1], [1, 2], [2, 2]], pm: { '1x1': 14, '1x2': 24, '2x2': 40 }, screw: 'M8', nut: 'Écrou marteau M8 rainure 8' },
  { s: 45, sizes: [[1, 1], [1, 2]], pm: { '1x1': 16, '1x2': 28 }, screw: 'M8', nut: 'Écrou marteau M8 rainure 10' },
];

const ALU = '#c9ced4';
const DARK = '#2f3338';

const out = [];

for (const { s, sizes, pm, screw, nut } of SERIES) {
  const family = `Série ${s}`;

  for (const [nx, ny] of sizes) {
    const w = s * nx;
    const h = s * ny;
    out.push({
      id: `alu-p${s}-${nx}x${ny}`,
      name: `Profilé ${w}×${h}`,
      category: 'alu',
      family,
      description: `Profilé aluminium à rainure, série ${s}, section ${w}×${h} mm. Extrudé selon X (0 → L), section centrée (Y = ${w}, Z = ${h}).`,
      ref: `P${s}-${w}x${h}`,
      material: 'Aluminium anodisé naturel',
      color: ALU,
      params: [{ key: 'L', label: 'Longueur', default: 500, min: 10, max: 6000, step: 1, unit: 'mm' }],
      parts: [{ type: 'extrude', section: `slot${s}-${nx}x${ny}`, length: 'L', axis: 'x' }],
      ports: [
        { id: 'debut', label: 'Extrémité début', pos: [0, 0, 0], dir: [-1, 0, 0] },
        { id: 'fin', label: 'Extrémité fin', pos: ['L', 0, 0], dir: [1, 0, 0] },
      ],
      bom: { kind: 'cut', label: '{L} mm', lengthExpr: 'L', pricePerM: pm[`${nx}x${ny}`], stock: `P${s} ${w}×${h}` },
    });
  }

  const e = { 20: 2.5, 30: 3, 40: 4, 45: 4.5 }[s];
  out.push({
    id: `alu-eq${s}`,
    name: `Équerre ${s}`,
    category: 'alu',
    family,
    description: `Équerre d'assemblage série ${s}. Coin intérieur à l'origine, ailes le long de +X et +Z, largeur ${s} centrée sur Y. Tourner pour les autres coins (Z 180°, X 180°, Y 180°).`,
    ref: `EQ${s}`,
    material: 'Aluminium moulé',
    color: '#aeb4bb',
    parts: [
      { type: 'box', name: 'aile X', size: [s, s, e], pos: [0, -s / 2, 0] },
      { type: 'box', name: 'aile Z', size: [e, s, s], pos: [0, -s / 2, 0] },
      { type: 'box', name: 'renfort', size: [s * 0.55, e, s * 0.55], pos: [0, -e / 2, 0], rot: [0, 0, 0] },
    ],
    ports: [{ id: 'coin', label: 'Coin intérieur', pos: [0, 0, 0] }],
    bom: { kind: 'piece', price: { 20: 1.2, 30: 1.6, 40: 2.4, 45: 2.8 }[s] },
    // Une vis par aile, tête côté intérieur, vers l'écrou marteau dans la rainure.
    fixings: [
      { type: 'vis', pos: [s * 0.62, 0, e], dir: [0, 0, 1], d: Number(screw.slice(1)), l: 12, ref: `VIS-${screw}x12`, name: `Vis tête cylindrique ${screw}×12` },
      { type: 'vis', pos: [e, 0, s * 0.62], dir: [1, 0, 0], d: Number(screw.slice(1)), l: 12, ref: `VIS-${screw}x12`, name: `Vis tête cylindrique ${screw}×12` },
    ],
    kit: [{ ref: `EM-${screw}-${s}`, name: nut, qty: 2, price: 0.35 }],
  });

  out.push({
    id: `alu-cap${s}`,
    name: `Embout ${s}×${s}`,
    category: 'alu',
    family,
    description: `Embout de fermeture ${s}×${s}, à poser en bout de profilé (port « p » sur l'extrémité, ép. 3 mm vers +X).`,
    ref: `CAP${s}`,
    material: 'Polyamide noir',
    color: DARK,
    parts: [{ type: 'box', size: [3, s, s], pos: [0, -s / 2, -s / 2] }],
    ports: [{ id: 'p', pos: [0, 0, 0], dir: [-1, 0, 0] }],
    bom: { kind: 'piece', price: 0.6 },
  });

  out.push({
    id: `alu-plj${s}`,
    name: `Plaque de liaison ${s}`,
    category: 'alu',
    family,
    description: `Plaque de liaison droite ${s * 3}×${s}, ép. 5, à visser à cheval sur deux profilés alignés. Origine au centre de la face arrière, plaque vers +Y.`,
    ref: `PLJ${s}`,
    material: 'Aluminium',
    color: '#aeb4bb',
    parts: [{ type: 'box', size: [s * 3, 5, s], pos: [-s * 1.5, 0, -s / 2] }],
    ports: [{ id: 'centre', pos: [0, 0, 0], dir: [0, -1, 0] }],
    bom: { kind: 'piece', price: { 20: 1.5, 30: 2, 40: 3, 45: 3.5 }[s] },
    fixings: [{
      type: 'vis', pos: [`(i-1.5)*${s * 0.75}`, 5, 0], dir: [0, 1, 0], d: Number(screw.slice(1)), l: 12,
      ref: `VIS-${screw}x12`, name: `Vis tête cylindrique ${screw}×12`, repeat: { count: 4, var: 'i' },
    }],
    kit: [{ ref: `EM-${screw}-${s}`, name: nut, qty: 4, price: 0.35 }],
  });
}

// ── Pieds, roulettes, panneaux (communs aux séries) ──────────────
out.push({
  id: 'alu-pied',
  name: 'Pied réglable',
  category: 'alu',
  family: 'Pieds et roulettes',
  description: "Pied à rotule réglable. Origine = haut de la tige (à visser dans l'alésage d'un profilé vertical) ; la semelle descend de H + 8 mm.",
  ref: 'PIED-M10',
  material: 'Acier zingué / polyamide',
  color: DARK,
  params: [
    { key: 'H', label: 'Longueur de tige', default: 40, min: 20, max: 150, unit: 'mm' },
    { key: 'D', label: 'Ø semelle', default: 60, options: [40, 60, 80], unit: 'mm' },
  ],
  parts: [
    { type: 'cyl', name: 'tige', r: 5, h: 'H', pos: [0, 0, '-H'], color: '#9aa0a6' },
    { type: 'cyl', name: 'semelle', r: 'D/2', h: 8, pos: [0, 0, '-H-8'] },
  ],
  ports: [
    { id: 'haut', label: 'Haut de tige', pos: [0, 0, 0], dir: [0, 0, 1] },
    { id: 'sol', label: 'Sol', pos: [0, 0, '-H-8'], dir: [0, 0, -1] },
  ],
  bom: { kind: 'piece', label: 'Ø{D}, tige {H}', price: 4.5 },
});

out.push({
  id: 'alu-roulette',
  name: 'Roulette pivotante Ø75',
  category: 'alu',
  family: 'Pieds et roulettes',
  description: "Roulette pivotante à platine (ou à tige M10), Ø75, hauteur totale 100. Origine = dessus de la platine (sous le profilé).",
  ref: 'ROUL-75',
  material: 'Acier / polyuréthane',
  color: DARK,
  params: [{ key: 'F', label: 'Frein (0/1)', default: 1, options: [0, 1] }],
  parts: [
    { type: 'box', name: 'platine', size: [60, 60, 4], pos: [-30, -30, -4], color: '#9aa0a6' },
    { type: 'box', name: 'chape', size: [30, 40, 50], pos: [-15, -20, -54], color: '#9aa0a6' },
    { type: 'cyl', name: 'roue', r: 37.5, h: 24, axis: 'y', pos: [0, -12, -62.5] },
  ],
  ports: [{ id: 'haut', pos: [0, 0, 0], dir: [0, 0, 1] }, { id: 'sol', pos: [0, 0, -100], dir: [0, 0, -1] }],
  bom: { kind: 'piece', label: '{F} frein', price: '9+F*3' },
  fixings: [{ type: 'vis', pos: ['-22+(i%2)*44', '-22+floor(i/2)*44', -4], dir: [0, 0, -1], d: 8, l: 16, ref: 'VIS-M8x16', name: 'Vis tête cylindrique M8×16', repeat: { count: 4, var: 'i' } }],
  kit: [{ ref: 'EM-M8-40', name: 'Écrou marteau M8 rainure 8', qty: 4, price: 0.35 }],
});

out.push({
  id: 'alu-panneau',
  name: 'Panneau de remplissage',
  category: 'alu',
  family: 'Panneaux',
  description: 'Panneau plan (polycarbonate, dibond, grillage…) à glisser en rainure. Vertical dans le plan XZ, épaisseur E centrée sur Y ; origine au coin bas-gauche.',
  ref: 'PANNEAU',
  material: 'Polycarbonate 4 mm',
  color: '#8fb3cf',
  params: [
    { key: 'W', label: 'Largeur', default: 500, min: 20, max: 3000, unit: 'mm' },
    { key: 'H', label: 'Hauteur', default: 500, min: 20, max: 3000, unit: 'mm' },
    { key: 'E', label: 'Épaisseur', default: 4, options: [2, 3, 4, 5, 6, 8, 10], unit: 'mm' },
  ],
  parts: [{ type: 'box', size: ['W', 'E', 'H'], pos: [0, '-E/2', 0] }],
  ports: [{ id: 'coin', pos: [0, 0, 0] }],
  bom: { kind: 'piece', label: '{W}×{H}×{E}', price: 'W*H/1e6*45' },
});

export default out;
